import React, { useState, useEffect } from 'react';
import { Globe, ExternalLink, Calendar, Tag } from 'lucide-react';
import CatholicApiService from '../services/catholicApi';
import ImageWithFallback from './ImageWithFallback';

const VaticanNews = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        const vaticanNews = await CatholicApiService.getVaticanNews();
        setNews(vaticanNews);
      } catch (error) {
        console.error('Erreur lors du chargement des actualités:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  if (loading) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement des actualités...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gradient-to-br from-blue-50 to-purple-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <Globe className="w-8 h-8 text-blue-600" />
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800">
              Actualités du Vatican
            </h2>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Restez informés des dernières nouvelles de l'Église universelle et des messages du Saint-Père.
          </p>
        </div>

        {/* Grille des actualités */}      

        {/* Section liens utiles */}
        <div className="mt-16 grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Globe className="w-8 h-8 text-yellow-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-3">Vatican News</h3>
            <p className="text-gray-600 text-sm mb-4">
              Suivez l'actualité officielle du Vatican en français
            </p>
            <a 
              href="https://www.vaticannews.va/fr.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              <span>Visiter le site</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Globe className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-3">Saint-Siège</h3>
            <p className="text-gray-600 text-sm mb-4">
              Documents officiels et enseignements pontificaux
            </p>
            <a 
              href="https://www.vatican.va/content/vatican/fr.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              <span>Site officiel</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Globe className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-3">AELF</h3>
            <p className="text-gray-600 text-sm mb-4">
              Textes liturgiques officiels en français
            </p>
            <a 
              href="https://www.aelf.org/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              <span>Découvrir</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>

        {/* Citation du Pape */}
        <div className="mt-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg shadow-lg p-8 max-w-4xl mx-auto">
          <div className="text-center">
            <blockquote className="text-xl italic mb-4">
              "L'Église n'est pas un musée pour les parfaits, mais un hôpital de campagne pour les blessés qui cherchent la guérison."
            </blockquote>
            <cite className="text-blue-200 font-medium">— Pape François</cite>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VaticanNews;